import _ from "lodash";
import qs from "query-string";
import { getTableConfig, saveTableConfig } from "../../common/actions";
import { entities } from "../../common/constants";
import { computeTableConfig } from "../../../utils/tableUtils";
import {
    buildBuildingViewFieldDefaults,
    mergeDynamicFieldsIntoDefaults,
    alignBuildingViewFieldConfigOrder,
    getBuildingViewFieldBand,
    BUILDING_VIEW_ALWAYS_VISIBLE_KEYS
} from "./buildingViewFieldConfig";
import {
    buildBuildingViewFieldCacheKey,
    getCachedBuildingViewTableSettings,
    setCachedBuildingViewTableSettings,
    invalidateBuildingViewFieldCache,
    hasCachedBuildingViewTableSettings,
    mergeBuildingViewTableSettings,
    getPersistedBuildingViewTableSettings,
    hasStoredBuildingViewTableSettings
} from "./buildingViewFieldConfigCache";

export const isExplicitlyHidden = value =>
    value === false || value === 0 || value === "0" || value === "false";

export const normalizeVisibilityValue = value => {
    if (isExplicitlyHidden(value)) return false;
    if (value === true || value === 1 || value === "1" || value === "true") return true;
    return undefined;
};

export const applyVisibilityOverrides = (fieldConfig, overrides = {}) => {
    if (!fieldConfig?.config || !overrides || typeof overrides !== "object") return fieldConfig;
    const config = { ...fieldConfig.config };
    Object.entries(overrides).forEach(([key, entry]) => {
        if (!entry || typeof entry !== "object") return;
        const visibility = normalizeVisibilityValue(entry.isVisible);
        if (visibility === undefined) return;
        config[key] = { ...(config[key] || {}), ...entry, isVisible: visibility };
    });
    return { ...fieldConfig, config };
};

export const buildBuildingViewTableSettingsPayload = (fieldConfig, dynamicBands = {}) => {
    const defaults = buildDefaultBuildingViewFieldConfig(dynamicBands);
    const config = {};
    Object.entries(fieldConfig?.config || {}).forEach(([key, entry]) => {
        const defaultVisible = !isExplicitlyHidden(defaults.config[key]?.isVisible);
        const currentVisible = !isExplicitlyHidden(entry?.isVisible);
        if (currentVisible !== defaultVisible) {
            config[key] = {
                isVisible: currentVisible,
                ...(entry?.pinned !== undefined ? { pinned: entry.pinned } : {}),
                ...(entry?.style ? { style: entry.style } : {})
            };
        }
    });
    return {
        keys: fieldConfig?.keys || [],
        config
    };
};

const resolveTableSettings = (cacheKey, { useMemoryCache = true, refetchResult = null } = {}) => {
    const memorySettings = useMemoryCache ? getCachedBuildingViewTableSettings(cacheKey) : undefined;
    const persistedSettings = getPersistedBuildingViewTableSettings(cacheKey);
    let tableSettings = refetchResult ?? memorySettings ?? persistedSettings ?? null;
    if (refetchResult && persistedSettings) {
        tableSettings = mergeBuildingViewTableSettings(refetchResult, persistedSettings);
    } else if (refetchResult == null && memorySettings && persistedSettings) {
        tableSettings = mergeBuildingViewTableSettings(memorySettings, persistedSettings);
    }
    return tableSettings;
};

export const getDynamicBandsSignature = (dynamicBands = {}) => {
    if (!dynamicBands || typeof dynamicBands !== "object") return "";
    return Object.entries(dynamicBands)
        .sort(([a], [b]) => String(a).localeCompare(String(b)))
        .map(([band, fields]) => {
            const names = (Array.isArray(fields) ? fields : [])
                .map(f => f?.field_name)
                .filter(Boolean)
                .sort()
                .join(",");
            return `${band}:[${names}]`;
        })
        .join("|");
};

export const bootstrapBuildingViewFieldConfig = (dynamicBands = {}, layoutContext = "view") =>
    alignBuildingViewFieldConfigOrder(
        buildDefaultBuildingViewFieldConfig(dynamicBands),
        dynamicBands,
        layoutContext
    );

export { getBuildingViewFieldBand };

export const getBuildingViewProjectId = search => {
    const parsed = typeof search === "string" ? qs.parse(search || "") || {} : search || {};
    const id = parsed.p_id ?? parsed.pid;
    return id != null && String(id) !== "" && String(id) !== "undefined" ? String(id) : null;
};

export const getRequiredBuildingViewFieldKeys = (role, buildingState = {}, dynamicBands = {}) => {
    const required = new Set(["name", "main_building_type_id"]);

    if (role === "super_admin") {
        required.add("consultancy_id");
    }
    if (role === "super_admin" || role === "consultancy_user") {
        required.add("client_id");
        required.add("region_id");
        required.add("site_id");
    }
    if ([true, "true"].includes(buildingState?.redline_completed)) {
        required.add("redline_completed_date");
    }

    if (dynamicBands && typeof dynamicBands === "object") {
        Object.values(dynamicBands).forEach(fields => {
            if (!Array.isArray(fields)) return;
            fields.forEach(field => {
                if (field?.rules?.mandatory && field?.field_name) {
                    required.add(`df__${field.field_name}`);
                }
            });
        });
    }

    return required;
};

export const enforceRequiredFieldVisibility = (fieldConfig, requiredKeys) => {
    const config = { ...fieldConfig.config };
    const forceVisible = new Set([...requiredKeys, ...BUILDING_VIEW_ALWAYS_VISIBLE_KEYS]);
    forceVisible.forEach(key => {
        if (config[key]) {
            config[key] = { ...config[key], isVisible: true };
        }
    });
    return { ...fieldConfig, config };
};

export const buildDefaultBuildingViewFieldConfig = (dynamicBands = {}) =>
    mergeDynamicFieldsIntoDefaults(buildBuildingViewFieldDefaults(), dynamicBands);

const applyBuildingViewFieldConfigFromSettings = (
    tableSettings,
    { dynamicBands, role, buildingState, layoutContext }
) => {
    const defaults = buildDefaultBuildingViewFieldConfig(dynamicBands);
    const requiredKeys = getRequiredBuildingViewFieldKeys(role, buildingState, dynamicBands);
    const { config: computedConfig, keys: computedKeys } = computeTableConfig({
        defaultConfig: defaults.config,
        defaultKeys: defaults.keys,
        tableSettings
    });
    const aligned = alignBuildingViewFieldConfigOrder({ keys: computedKeys, config: computedConfig }, dynamicBands, layoutContext);
    const withVisibility = applyVisibilityOverrides(aligned, tableSettings?.config);
    return enforceRequiredFieldVisibility(withVisibility, requiredKeys);
};

export const resolveBuildingViewFieldConfig = async ({
    userId,
    projectId,
    clientId,
    dynamicBands = {},
    role,
    buildingState,
    layoutContext = "view",
    refetch = false
}) => {
    const defaults = buildDefaultBuildingViewFieldConfig(dynamicBands);
    const requiredKeys = getRequiredBuildingViewFieldKeys(role, buildingState, dynamicBands);

    if (!userId || !projectId || !clientId) {
        const fieldConfig = enforceRequiredFieldVisibility(
            alignBuildingViewFieldConfigOrder(defaults, dynamicBands, layoutContext),
            requiredKeys
        );
        return { fieldConfig, fromCache: false, cacheKey: null };
    }

    const cacheKey = buildBuildingViewFieldCacheKey({ userId, projectId, clientId });
    const useCache = !refetch && hasStoredBuildingViewTableSettings(cacheKey);
    let tableSettings;

    if (useCache) {
        tableSettings = resolveTableSettings(cacheKey, { useMemoryCache: true });
    } else {
        const previousSettings = resolveTableSettings(cacheKey, { useMemoryCache: true });
        const response = await getTableConfig({
            user_id: userId,
            project_id: projectId,
            client_id: clientId,
            table_type: entities.BUILDING_VIEW
        });
        const apiSettings = response?.data?.table_settings ?? null;
        tableSettings = mergeBuildingViewTableSettings(apiSettings, previousSettings);
        setCachedBuildingViewTableSettings(cacheKey, tableSettings);
    }

    const fieldConfig = applyBuildingViewFieldConfigFromSettings(tableSettings, {
        dynamicBands,
        role,
        buildingState,
        layoutContext
    });
    return { fieldConfig, fromCache: useCache, cacheKey };
};

export const recomputeBuildingViewFieldConfig = ({
    userId,
    projectId,
    clientId,
    dynamicBands = {},
    role,
    buildingState,
    layoutContext = "view"
}) => {
    const cacheKey = buildBuildingViewFieldCacheKey({ userId, projectId, clientId });
    const tableSettings = resolveTableSettings(cacheKey);
    return applyBuildingViewFieldConfigFromSettings(tableSettings, {
        dynamicBands,
        role,
        buildingState,
        layoutContext
    });
};

export const loadBuildingViewFieldConfig = async params => {
    const { fieldConfig } = await resolveBuildingViewFieldConfig({ ...params, refetch: params.refetch ?? false });
    return fieldConfig;
};

export const saveBuildingViewFieldConfig = async ({ userId, projectId, clientId, keys, config, dynamicBands = {} }) => {
    if (!userId || !projectId || !clientId) return;
    const table_settings = buildBuildingViewTableSettingsPayload({ keys, config }, dynamicBands);
    await saveTableConfig({
        user_id: userId,
        project_id: projectId,
        client_id: clientId,
        table_type: entities.BUILDING_VIEW,
        table_settings
    });
    setCachedBuildingViewTableSettings(buildBuildingViewFieldCacheKey({ userId, projectId, clientId }), table_settings);
};

export const resetBuildingViewFieldConfig = async ({ userId, projectId, clientId }) => {
    if (!userId || !projectId || !clientId) return null;
    await saveTableConfig({
        user_id: userId,
        project_id: projectId,
        client_id: clientId,
        table_type: entities.BUILDING_VIEW,
        table_settings: null
    });
    setCachedBuildingViewTableSettings(buildBuildingViewFieldCacheKey({ userId, projectId, clientId }), null);
    return null;
};

export const isBuildingViewFieldVisible = (key, fieldConfig) => {
    if (BUILDING_VIEW_ALWAYS_VISIBLE_KEYS.has(key)) return true;
    return !isExplicitlyHidden(fieldConfig?.config?.[key]?.isVisible);
};

export const getHiddenBuildingViewFieldCount = fieldConfig => {
    if (!fieldConfig?.config) return 0;
    return Object.keys(fieldConfig.config).filter(
        key => !BUILDING_VIEW_ALWAYS_VISIBLE_KEYS.has(key) && isExplicitlyHidden(fieldConfig.config[key]?.isVisible)
    ).length;
};

export const hasActiveBuildingViewFieldCustomization = fieldConfig =>
    getHiddenBuildingViewFieldCount(fieldConfig) > 0;

export const isBuildingViewFieldConfigChanged = (current, initial) => {
    if (!current?.config || !initial?.config) return false;
    const keys = new Set([...Object.keys(current.config), ...Object.keys(initial.config)]);
    for (const key of keys) {
        if (!_.isEqual(
            !isExplicitlyHidden(current.config[key]?.isVisible),
            !isExplicitlyHidden(initial.config[key]?.isVisible)
        )) {
            return true;
        }
    }
    return false;
};

export const isDynamicBandVisible = (fields, fieldConfig) => {
    if (!Array.isArray(fields) || !fields.length) return false;
    return fields.some(f => f?.field_name && isBuildingViewFieldVisible(`df__${f.field_name}`, fieldConfig));
};

const bandVisibilityExcludedKeys = BUILDING_VIEW_ALWAYS_VISIBLE_KEYS;

export const isBuildingViewBandVisible = (bandName, fieldConfig, layoutContext = "view") => {
    if (!bandName || !fieldConfig?.config) return false;
    return (fieldConfig.keys || []).some(key => {
        const entry = fieldConfig.config[key];
        if (!entry || bandVisibilityExcludedKeys.has(key)) return false;
        if (entry.isDynamicField || String(key).startsWith("df__")) return false;
        return getBuildingViewFieldBand(key, fieldConfig, layoutContext) === bandName && isBuildingViewFieldVisible(key, fieldConfig);
    });
};

export const isBuildingViewFieldConfigReady = (fieldConfig, configReady) =>
    !!configReady && !!fieldConfig?.config;
